import { useParams, Link, useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import {
  Heart,
  Share2,
  MapPin,
  Eye,
  Clock,
  CheckCircle,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  Truck,
  CreditCard,
  ShieldCheck,
  AlertTriangle,
  Mail,
  ShoppingCart,
  Package,
  Car,
} from 'lucide-react'
import { useState, useEffect, useCallback } from 'react'
import { clsx } from 'clsx'
import { useListing, useSavedItems } from '@/hooks'
import { useAuthStore } from '@/store/authStore'
import { useUIStore } from '@/store/uiStore'
import { Button } from '@/components/common/Button'
import { ListingPageSkeleton } from '@/components/common/Skeleton'
import { BidSection, RelatedListings, SafetyTips, ImageLightbox, ListingStatusControls, MakeOfferSection, ReviewSection } from '@/components/listings'
import { EnquiryForm } from '@/components/listings/EnquiryForm'
import { getUserProfile } from '@/services/api/users'
import { useCartActions } from '@/hooks/useCart'
import type { UserProfile } from '@/types/user'
import { FundraiserSection } from '@/components/fundraiser'
import { StorefrontPreviewCard } from '@/components/storefront/StorefrontPreviewCard'
import { SEO } from '@/components/common/SEO'
import { AdCarousel } from '@/components/ads/AdCarousel'
import { FinanceCalculator } from '@/components/listings/FinanceCalculator'
import {
  formatPrice,
  formatRelativeTime,
  formatTimeRemaining,
  formatLocation,
  formatOdometer,
} from '@/utils/format'
import { getCategoryById } from '@/utils/categories'
import {
  isVehicleListing,
  isFundraiserListing,
  isAuctionListing,
  type Listing,
  type VehicleListing,
  type FundraiserListing,
} from '@/types/listing'

export function ListingPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { listing, isLoading, error } = useListing(id)
  const { isSaved, toggleSave } = useSavedItems()
  const { isAuthenticated, user } = useAuthStore()
  const { openModal, showToast } = useUIStore()
  const [activeImageIndex, setActiveImageIndex] = useState(0)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [failedImages, setFailedImages] = useState<Set<number>>(new Set())
  const [sellerProfile, setSellerProfile] = useState<UserProfile | null>(null)
  const [cartQty, setCartQty] = useState(1)
  const { add: addToCart } = useCartActions()

  // Image navigation hooks — must be before early returns
  const nextImage = useCallback(() => {
    setActiveImageIndex((prev) =>
      prev < (listing?.images?.length || 1) - 1 ? prev + 1 : 0
    )
  }, [listing?.images?.length])

  const prevImage = useCallback(() => {
    setActiveImageIndex((prev) =>
      prev > 0 ? prev - 1 : (listing?.images?.length || 1) - 1
    )
  }, [listing?.images?.length])

  // Keyboard navigation for image gallery
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') prevImage()
      else if (e.key === 'ArrowRight') nextImage()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [nextImage, prevImage])

  useEffect(() => {
    if (listing?.sellerId) {
      getUserProfile(listing.sellerId).then(setSellerProfile).catch(() => {})
    }
  }, [listing?.sellerId])

  if (isLoading) {
    return <ListingPageSkeleton />
  }

  if (error || !listing) {
    return (
      <div className="container py-12 text-center">
        <div className="text-6xl mb-4" aria-hidden="true">😕</div>
        <h1 className="text-2xl font-bold mb-2">Listing Not Found</h1>
        <p className="text-slate-500 mb-6">
          This listing may have been removed or doesn&apos;t exist.
        </p>
        <Link to="/browse">
          <Button>Browse Listings</Button>
        </Link>
      </div>
    )
  }

  const saved = isSaved(listing.id)
  const category = getCategoryById(listing.category)
  const isAuction = isAuctionListing(listing)
  const isOwner = isAuthenticated && user?.uid === listing.sellerId
  const isBuyNow = !isAuction

  // E-commerce mode: listing has 'product' sale type
  const isProduct = listing.saleType === 'product'
  const isInStock = isProduct && (listing.stock ?? 0) > 0
  const isOutOfStock = isProduct && (listing.stock ?? 0) === 0
  const lowStock = isProduct && (listing.stock ?? 0) > 0 && (listing.stock ?? 0) <= 5

  // Vehicle Buy Now: general seller vehicles only (not dealerships)
  const isVehicleBuyNow = isVehicleListing(listing) && !isAuction && sellerProfile?.subscriptionTier !== 'dealership'

  const handleAddToCart = () => {
    if (!isAuthenticated) {
      openModal('login')
      return
    }
    if (!listing || !isInStock) return
    addToCart({
      listingId: listing.id,
      sellerId: listing.sellerId,
      price: listing.price ?? 0,
      title: listing.title,
      image: listing.images?.[0] || '',
      quantity: cartQty,
    })
    setCartQty(1)
  }

  const handleBuyNowVehicle = async () => {
    if (!isAuthenticated) {
      openModal('login')
      return
    }
    if (!listing || !listing.price) return
    await addToCart({
      listingId: listing.id,
      sellerId: listing.sellerId,
      price: listing.price,
      title: listing.title,
      image: listing.images?.[0] || '',
      quantity: 1,
    })
    navigate('/checkout')
  }

  const handleSave = () => {
    if (!isAuthenticated) {
      openModal('login')
      return
    }
    toggleSave(listing.id)
  }

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: listing.title,
        text: `Check out ${listing.title} on iSQROLL`,
        url: window.location.href,
      })
    } else {
      navigator.clipboard.writeText(window.location.href)
      showToast('Link copied to clipboard!')
    }
  }

  const handleContact = () => {
    if (!isAuthenticated) {
      openModal('login')
      return
    }
    openModal('message', {
      listingId: listing.id,
      sellerId: listing.sellerId,
      sellerName: listing.sellerName,
      listingTitle: listing.title,
      listingImage: listing.images?.[0],
      listingPrice: listing.price ?? undefined,
      listingType: listing.listingType,
    })
  }

  return (
    <div className="pb-36 md:pb-8">
      <SEO
        title={listing.title}
        description={listing.description?.slice(0, 160)}
        image={listing.images?.[0]}
        type="product"
      />
      {listing && (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Product",
            "name": listing.title,
            "description": listing.description?.slice(0, 300),
            "image": listing.images?.[0] || '',
            ...(listing.price != null && listing.price > 0 ? {
              "offers": {
                "@type": "Offer",
                "price": listing.price,
                "priceCurrency": "NZD",
                "availability": listing.status === 'active' ? "https://schema.org/InStock" : "https://schema.org/SoldOut",
                "itemCondition": listing.condition === 'new' ? "https://schema.org/NewCondition" : "https://schema.org/UsedCondition"
              }
            } : {})
          })}</script>
        </Helmet>
      )}

      {/* Breadcrumb */}
      <div className="bg-white border-b border-slate-200">
        <div className="container py-3">
          <div className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-slate-500 hover:text-slate-700">
              Home
            </Link>
            <span className="text-slate-300">/</span>
            <Link to="/browse" className="text-slate-500 hover:text-slate-700">
              Browse
            </Link>
            {category && (
              <>
                <span className="text-slate-300">/</span>
                <Link
                  to={`/browse?category=${category.id}`}
                  className="text-slate-500 hover:text-slate-700"
                >
                  {category.name}
                </Link>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="container py-4 md:py-6">
        <div className="grid lg:grid-cols-2 gap-4 md:gap-8">
          {/* Image Gallery */}
          <div className="min-w-0">
            <div className="relative aspect-[4/3] bg-slate-100 rounded-xl overflow-hidden mb-4">
              {listing.images?.[activeImageIndex] && !failedImages.has(activeImageIndex) ? (
                <img
                  src={listing.images[activeImageIndex]}
                  alt={listing.title}
                  className="w-full h-full object-cover cursor-pointer"
                  loading="eager"
                  fetchPriority="high"
                  decoding="async"
                  onClick={() => setLightboxOpen(true)}
                  onError={() => setFailedImages((prev) => new Set(prev).add(activeImageIndex))}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-400">
                  <span className="text-6xl" aria-label="No image available" role="img">📷</span>
                </div>
              )}

              {/* Navigation arrows */}
              {listing.images && listing.images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/80 rounded-full hover:bg-white transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                    aria-label="Previous image"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/80 rounded-full hover:bg-white transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                    aria-label="Next image"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </>
              )}

              {/* Badges */}
              <div className="absolute top-4 left-4 flex gap-2">
                {isAuction && (
                  <span className="badge bg-purple-500 text-white">Auction</span>
                )}
              </div>

              {/* Image counter */}
              {listing.images && listing.images.length > 1 && (
                <div className="absolute bottom-4 right-4 bg-black/70 text-white text-sm px-3 py-1 rounded-full">
                  {activeImageIndex + 1} / {listing.images.length}
                </div>
              )}
            </div>

            {/* Thumbnails */}
            {listing.images && listing.images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-2 scroll-smooth">
                {listing.images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveImageIndex(index)}
                    className={clsx(
                      'flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-colors',
                      activeImageIndex === index
                        ? 'border-primary-500'
                        : 'border-transparent hover:border-slate-300'
                    )}
                  >
                    {!failedImages.has(index) ? (
                      <img
                        src={image}
                        alt={`${listing.title} ${index + 1}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        decoding="async"
                        onError={() => setFailedImages((prev) => new Set(prev).add(index))}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-slate-100 text-slate-400 text-sm" aria-label="Image unavailable" role="img">📷</div>
                    )}
                  </button>
                ))}
              </div>
            )}

            {/* Business Listing Banner (desktop — between images and description) */}
            {sellerProfile && sellerProfile.subscriptionTier !== 'general' && sellerProfile.listingBannerUrl && (
              <div className="hidden lg:block mt-4 mb-2">
                <img
                  src={sellerProfile.listingBannerUrl}
                  alt={`${sellerProfile.displayName || 'Seller'} banner`}
                  className="w-full rounded-xl object-contain max-h-48"
                />
              </div>
            )}

            {/* Description — below images on left column (desktop) */}
            <div className="hidden lg:block mt-6">
              <div className="card p-6 mb-6">
                <h2 className="text-lg font-semibold mb-3">Description</h2>
                <div className="prose prose-slate max-w-none">
                  <p className="whitespace-pre-wrap text-slate-600 leading-relaxed break-words">
                    {listing.description}
                  </p>
                </div>

                {/* Condition */}
                {listing.condition && (
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-500">Condition:</span>
                      <span className="badge-primary capitalize">{listing.condition}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Vehicle Details (desktop) */}
              {isVehicleListing(listing) && (
                <VehicleDetails listing={listing as VehicleListing} />
              )}

              {/* Fundraiser Section (desktop) */}
              {isFundraiserListing(listing) && (
                <FundraiserSection listing={listing as FundraiserListing} />
              )}

            </div>
          </div>

          {/* ════════════════════════════════════════════════════════════ */}
          {/* RIGHT COLUMN                                               */}
          {/* Mobile: flex-col + order classes reorder sections           */}
          {/* Desktop: lg:order-none resets to source order               */}
          {/* ════════════════════════════════════════════════════════════ */}
          <div className="min-w-0 flex flex-col">

            {/* ── 1. Business Listing Banner (mobile only — desktop banner is in left column) ── */}
            {sellerProfile && sellerProfile.subscriptionTier !== 'general' && sellerProfile.listingBannerUrl && (
              <div className="order-1 lg:hidden mb-4">
                <img
                  src={sellerProfile.listingBannerUrl}
                  alt={`${sellerProfile.displayName || 'Seller'} banner`}
                  className="w-full rounded-xl object-contain max-h-32"
                />
              </div>
            )}

            {/* ── 2. Price, Title, Meta ── */}
            <div className="order-2 lg:order-[1]">
              {/* Price */}
              <div className="mb-4">
                {isAuction ? (
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Current Bid</p>
                    <p className="text-2xl md:text-3xl font-bold text-slate-900">
                      {formatPrice(
                        (listing as Listing & { currentBid?: number }).currentBid ||
                          listing.price
                      )}
                    </p>
                    {(listing as Listing & { bidCount?: number }).bidCount ? (
                      <p className="text-sm text-slate-500">
                        {(listing as Listing & { bidCount?: number }).bidCount} bids
                      </p>
                    ) : (
                      <p className="text-sm text-slate-500">No bids yet</p>
                    )}
                    {listing.expiresAt && (
                      <div className="flex items-center gap-1 text-sm text-purple-600 mt-2">
                        <Clock className="w-4 h-4" />
                        {formatTimeRemaining(listing.expiresAt)}
                      </div>
                    )}
                  </div>
                ) : isFundraiserListing(listing) ? (
                  <div>
                    <p className="text-2xl md:text-3xl font-bold text-primary-600">
                      Fundraiser
                    </p>
                    {(listing as FundraiserListing).organizationName && (
                      <p className="text-sm text-slate-500 mt-1">
                        by {(listing as FundraiserListing).organizationName}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-2xl md:text-3xl font-bold text-slate-900">
                    {formatPrice(listing.price)}
                  </p>
                )}
              </div>

              {/* Title */}
              <h1 className="text-xl md:text-2xl font-bold text-slate-900 mb-3 break-words">
                {listing.title}
              </h1>

              {/* Meta info */}
              <div className="flex flex-wrap gap-3 md:gap-4 text-sm text-slate-500 mb-4">
                <div className="flex items-center gap-1">
                  <MapPin className="w-4 h-4" />
                  {formatLocation(listing.location, listing.suburb)}
                </div>
                <div className="flex items-center gap-1">
                  <Eye className="w-4 h-4" />
                  {listing.views || 0} views
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  Listed {formatRelativeTime(listing.createdAt)}
                </div>
              </div>

              {/* Seller email (general accounts with showEmailOnListings) */}
              {sellerProfile?.showEmailOnListings && sellerProfile.email && (
                <div className="flex items-center gap-1 text-sm text-slate-500 mb-4">
                  <Mail className="w-4 h-4" />
                  <a href={`mailto:${sellerProfile.email}`} className="hover:text-primary-600">{sellerProfile.email}</a>
                </div>
              )}
            </div>

            {/* ── 3. Mobile-only: Description, Condition, Vehicle/Fundraiser details ── */}
            <div className="order-3 lg:hidden">
              {/* Description */}
              <div className="card p-4 sm:p-5 mb-4">
                <h2 className="text-lg font-semibold mb-3">Description</h2>
                <p className="whitespace-pre-wrap text-slate-600 leading-relaxed break-words">
                  {listing.description}
                </p>
                {listing.condition && (
                  <div className="mt-4 pt-4 border-t border-slate-100">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-slate-500">Condition:</span>
                      <span className="badge-primary capitalize">{listing.condition}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Vehicle Details (mobile) */}
              {isVehicleListing(listing) && (
                <VehicleDetails listing={listing as VehicleListing} />
              )}

              {/* Fundraiser Section (mobile) */}
              {isFundraiserListing(listing) && (
                <div className="mb-6">
                  <FundraiserSection listing={listing as FundraiserListing} />
                </div>
              )}
            </div>

            {/* ── 4. View Store (StorefrontPreviewCard) ── */}
            <div className="order-4 lg:order-[2] mb-4">
              <StorefrontPreviewCard
                sellerId={listing.sellerId}
                sellerName={listing.sellerName || 'Seller'}
                verified={listing.sellerVerified}
                rating={listing.sellerRating}
                listingType={listing.listingType}
              />
            </div>

            {/* ── 4b. Agent info (dealership listings) ── */}
            {listing.agentName && (
              <div className="order-4 lg:order-[2] mb-4">
                <div className="card p-4 flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-slate-200 text-slate-500 flex items-center justify-center text-sm font-bold flex-shrink-0 overflow-hidden">
                    {listing.agentPhotoUrl ? (
                      <img src={listing.agentPhotoUrl} alt={listing.agentName} className="w-full h-full object-cover" />
                    ) : (
                      listing.agentName[0]?.toUpperCase()
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{listing.agentName}</p>
                    {listing.agentRole && <p className="text-xs text-slate-500">{listing.agentRole}</p>}
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    {listing.agentPhone && (
                      <a href={`tel:${listing.agentPhone}`} className="text-xs text-primary-600 hover:underline">{listing.agentPhone}</a>
                    )}
                    {listing.agentEmail && (
                      <a href={`mailto:${listing.agentEmail}`} className="text-xs text-primary-600 hover:underline">Email</a>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* ── 5. Contact, Bidding, Cart, Offers, Actions ── */}
            <div className="order-5 lg:order-[3]">
              {/* Auction Bidding Section */}
              {isAuction && (
                <div className="mb-4">
                  <BidSection listing={listing} />

                  {/* Buy Now option on auctions */}
                  {!isOwner && listing.status === 'active' && (listing as Listing & { buyNowPrice?: number }).buyNowPrice && !(listing as Listing & { auctionEnded?: boolean }).auctionEnded && (
                    <div className="mt-4 p-3 sm:p-4 bg-primary-50 border border-primary-200 rounded-lg">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div>
                          <p className="text-sm font-medium text-primary-800">Buy Now</p>
                          <p className="text-lg font-bold text-primary-900">
                            {formatPrice((listing as Listing & { buyNowPrice?: number }).buyNowPrice)}
                          </p>
                          <p className="text-xs text-primary-600">Skip the auction and buy instantly</p>
                        </div>
                        <Button
                          variant="primary"
                          onClick={handleContact}
                          leftIcon={<MessageSquare className="w-4 h-4" />}
                          className="w-full sm:w-auto"
                        >
                          Buy Now
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Stock indicator (e-commerce listings) */}
              {isProduct && (
                <div className="mb-4">
                  {isOutOfStock ? (
                    <div className="flex items-center gap-2 text-red-600">
                      <Package className="w-4 h-4" />
                      <span className="text-sm font-medium">Out of Stock</span>
                    </div>
                  ) : lowStock ? (
                    <div className="flex items-center gap-2 text-amber-600">
                      <Package className="w-4 h-4" />
                      <span className="text-sm font-medium">Low stock — {listing.stock} left</span>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-green-600">
                      <Package className="w-4 h-4" />
                      <span className="text-sm font-medium">{listing.stock} in stock</span>
                    </div>
                  )}
                  {listing.sold != null && listing.sold > 0 && (
                    <p className="text-xs text-slate-500 mt-1">{listing.sold} sold</p>
                  )}
                </div>
              )}

              {/* Add to Cart (e-commerce mode, non-owner, active) */}
              {isProduct && !isOwner && listing.status === 'active' && (
                <div className="mb-4 space-y-3">
                  {sellerProfile?.stripeConnectStatus !== 'active' ? (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                      This seller has not set up payment processing yet. Please contact them directly to arrange purchase.
                    </div>
                  ) : isInStock ? (
                    <>
                      <div className="flex items-center gap-3">
                        <label className="text-sm font-medium text-slate-700">Qty:</label>
                        <select
                          value={cartQty}
                          onChange={(e) => setCartQty(Number(e.target.value))}
                          className="input w-20 !py-1.5"
                        >
                          {Array.from(
                            { length: Math.min(listing.stock ?? 1, 10) },
                            (_, i) => i + 1
                          ).map((n) => (
                            <option key={n} value={n}>{n}</option>
                          ))}
                        </select>
                      </div>
                      <div className="hidden md:flex gap-3">
                        <Button
                          variant="primary"
                          className="flex-1"
                          leftIcon={<ShoppingCart className="w-4 h-4" />}
                          onClick={handleAddToCart}
                        >
                          Add to Cart
                        </Button>
                      </div>
                    </>
                  ) : (
                    <Button variant="secondary" disabled className="w-full">
                      Out of Stock
                    </Button>
                  )}
                </div>
              )}

              {/* Buy Now (vehicle listings from general sellers) */}
              {isVehicleBuyNow && !isOwner && listing.status === 'active' && listing.price && listing.price > 0 && (
                <div className="mb-4">
                  {sellerProfile?.stripeConnectStatus !== 'active' ? (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                      This seller has not set up payment processing yet. Please contact them directly to arrange purchase.
                    </div>
                  ) : (
                    <div className="hidden md:flex gap-3">
                      <Button
                        variant="primary"
                        className="flex-1"
                        leftIcon={<CreditCard className="w-4 h-4" />}
                        onClick={handleBuyNowVehicle}
                      >
                        Buy Now
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Make Offer (buyers only, classifieds-mode fixed price listings — hidden for fundraisers & dealerships) */}
              {!isOwner && isBuyNow && !isProduct && listing.status === 'active' && !isFundraiserListing(listing) && sellerProfile?.subscriptionTier !== 'dealership' && (
                <div className="mb-4">
                  <MakeOfferSection listing={listing} />
                </div>
              )}

              {/* Action buttons */}
              <div className="hidden md:flex gap-3 mb-6">
                {!isProduct && sellerProfile?.enableChat !== false && (
                  <Button
                    variant="primary"
                    className="flex-1"
                    leftIcon={<MessageSquare className="w-4 h-4" />}
                    onClick={handleContact}
                  >
                    {isFundraiserListing(listing) ? 'Contact Fundraiser' : 'Contact Seller'}
                  </Button>
                )}
                <Button
                  variant={saved ? 'primary' : 'secondary'}
                  onClick={handleSave}
                  leftIcon={
                    <Heart className={clsx('w-4 h-4', saved && 'fill-current')} />
                  }
                >
                  {saved ? 'Saved' : 'Save'}
                </Button>
                <Button variant="secondary" onClick={handleShare}>
                  <Share2 className="w-4 h-4" />
                </Button>
              </div>

              {/* Mobile action row (save + share only, contact is in sticky bar) */}
              <div className="flex gap-3 mb-4 md:hidden">
                <Button
                  variant={saved ? 'primary' : 'secondary'}
                  className="flex-1"
                  onClick={handleSave}
                  leftIcon={
                    <Heart className={clsx('w-4 h-4', saved && 'fill-current')} />
                  }
                >
                  {saved ? 'Saved' : 'Save'}
                </Button>
                <Button variant="secondary" className="flex-1" onClick={handleShare}>
                  <Share2 className="w-4 h-4 mr-1" />
                  Share
                </Button>
              </div>
            </div>

            {/* ── Finance Calculator (right column, above Safety Tips) ── */}
            {isVehicleListing(listing) && listing.price && listing.price > 0 && (
              <div className="order-5 lg:order-[4] mb-6">
                <FinanceCalculator
                  price={listing.price}
                  defaultRate={sellerProfile?.financeRate || undefined}
                  dealerName={sellerProfile?.subscriptionTier === 'dealership' ? sellerProfile.displayName : undefined}
                  dealerLogo={sellerProfile?.subscriptionTier === 'dealership' ? sellerProfile.logoUrl : undefined}
                  providerName={sellerProfile?.financeProviderName}
                  providerLogo={sellerProfile?.financeProviderLogo}
                  providerUrl={sellerProfile?.financeProviderUrl}
                  contactEmail={sellerProfile?.financeContactEmail}
                  contactPhone={sellerProfile?.financeContactPhone}
                  minAmount={sellerProfile?.financeMinAmount}
                  termsUrl={sellerProfile?.financeTermsUrl}
                />
              </div>
            )}

            {/* ── 6. Safety Tips ── */}
            <div className="order-6 lg:order-[5] mb-6">
              <SafetyTips />
            </div>

            {/* ── 7. Reviews ── */}
            <div className="order-7 lg:order-[9] mb-6">
              <ReviewSection
                sellerId={listing.sellerId}
                sellerName={listing.sellerName || 'Seller'}
                listingId={listing.id}
              />
            </div>

            {/* ── 8. Enquiry Form (mobile: before safety tips, desktop: after actions) ── */}
            {sellerProfile?.enableEnquiryForm && sellerProfile.email && !isOwner && (
              <div className="order-[5] lg:order-[4] mb-4">
                <EnquiryForm
                  listingId={listing.id}
                  listingTitle={listing.title}
                  listingImage={listing.images?.[0]}
                  listingPrice={listing.price}
                  sellerId={listing.sellerId}
                  sellerName={listing.sellerName || 'Seller'}
                  sellerEmail={sellerProfile.email}
                />
              </div>
            )}

            {/* ── 9. Shipping, Status Controls ── */}
            <div className="order-8 lg:order-[4]">
              {/* Shipping & Payment */}
              <ShippingPaymentSection listing={listing} />

              {/* Status Controls (owner only) */}
              {isOwner && (
                <div className="mb-6">
                  <ListingStatusControls listing={listing} />
                </div>
              )}
            </div>

          </div>
        </div>

        {/* Related Listings */}
        <div className="mt-12 border-t border-slate-200 pt-12">
          <RelatedListings
            currentListingId={listing.id}
            category={listing.category}
            sellerId={listing.sellerId}
          />
        </div>

        {/* Sponsor ad */}
        <div className="mt-8">
          <AdCarousel placement="listing" />
        </div>
      </div>

      {/* Mobile sticky buy bar */}
      <div className="fixed bottom-[60px] left-0 right-0 bg-white border-t border-slate-200 px-4 py-3 flex items-center justify-between md:hidden z-30">
        <div className="min-w-0 mr-3">
          <p className="text-lg font-bold text-slate-900 truncate">{isFundraiserListing(listing) ? 'Fundraiser' : formatPrice(listing.price)}</p>
          <p className="text-xs text-slate-500 truncate">{listing.title}</p>
        </div>
        {isProduct && sellerProfile?.stripeConnectStatus !== 'active' && !isOwner ? (
          <span className="text-xs text-amber-600 font-medium">Payments not set up</span>
        ) : isProduct && isInStock && !isOwner ? (
          <Button variant="primary" onClick={handleAddToCart} size="sm" className="flex-shrink-0">
            <ShoppingCart className="w-4 h-4 mr-1.5" />
            Add to Cart
          </Button>
        ) : isProduct && isOutOfStock ? (
          <Button variant="secondary" disabled size="sm" className="flex-shrink-0">
            Out of Stock
          </Button>
        ) : isVehicleBuyNow && !isOwner && listing.price && listing.price > 0 && sellerProfile?.stripeConnectStatus === 'active' ? (
          <Button variant="primary" onClick={handleBuyNowVehicle} size="sm" className="flex-shrink-0">
            <CreditCard className="w-4 h-4 mr-1.5" />
            Buy Now
          </Button>
        ) : sellerProfile?.enableChat !== false ? (
          <Button variant="primary" onClick={handleContact} size="sm" className="flex-shrink-0">
            <MessageSquare className="w-4 h-4 mr-1.5" />
            Contact
          </Button>
        ) : null}
      </div>

      {/* Lightbox */}
      {lightboxOpen && listing.images && listing.images.length > 0 && (
        <ImageLightbox
          images={listing.images}
          initialIndex={activeImageIndex}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  )
}

// Vehicle-specific details component
function VehicleDetails({ listing }: { listing: VehicleListing }) {
  const keySpecs = [
    { label: 'Year', value: listing.year?.toString() },
    { label: 'Odometer', value: formatOdometer(listing.odometer) },
    { label: 'Transmission', value: listing.transmission },
    { label: 'Fuel Type', value: listing.fuelType },
  ].filter((spec) => spec.value)

  const allSpecs = [
    { label: 'Make', value: listing.make },
    { label: 'Model', value: listing.model },
    { label: 'Year', value: listing.year?.toString() },
    { label: 'Odometer', value: formatOdometer(listing.odometer) },
    { label: 'Colour', value: listing.colour },
    { label: 'Body Type', value: listing.bodyType },
    { label: 'Doors', value: listing.doors?.toString() },
    { label: 'Engine', value: listing.engineCC ? `${listing.engineCC}cc` : undefined },
    { label: 'Fuel Type', value: listing.fuelType },
    { label: 'Transmission', value: listing.transmission },
    { label: 'Previous Owners', value: listing.previousOwners },
    { label: 'WOF Expiry', value: listing.wofExpiry },
    { label: 'Rego Expiry', value: listing.regoExpiry },
  ].filter((spec) => spec.value)

  const hasBackgroundChecks = listing.stolenCheck || listing.damageCheck

  return (
    <div className="card mb-6 overflow-hidden">
      <div className="bg-slate-800 text-white px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-3">
        <Car className="w-5 h-5 text-primary-400" />
        <h2 className="text-base sm:text-lg font-semibold">Vehicle Details</h2>
      </div>

      {/* Key specs highlight row */}
      {keySpecs.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 border-b border-slate-200">
          {keySpecs.map((spec) => (
            <div key={spec.label} className="px-3 sm:px-4 py-3 text-center border-b sm:border-b-0 sm:border-r last:border-r-0 last:border-b-0 border-slate-200">
              <p className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">{spec.label}</p>
              <p className="text-sm sm:text-base font-semibold text-slate-900 mt-0.5">{spec.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Full specs grid */}
      {allSpecs.length > 0 && (
        <div className="divide-y divide-slate-100">
          {allSpecs.map((spec) => (
            <div key={spec.label} className="flex items-center justify-between px-4 sm:px-6 py-2.5">
              <span className="text-sm text-slate-500">{spec.label}</span>
              <span className="text-sm font-medium text-slate-900 text-right">{spec.value}</span>
            </div>
          ))}
        </div>
      )}

      {/* Background Checks */}
      {hasBackgroundChecks && (
        <div className="border-t border-slate-200 bg-slate-50 px-4 sm:px-6 py-3 sm:py-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2 text-sm">
            <ShieldCheck className="w-5 h-5 text-primary-500" />
            Background Checks
          </h3>
          <div className="space-y-2">
            {listing.stolenCheck && (
              <div className="flex items-center justify-between bg-white rounded-lg px-3 sm:px-4 py-2.5 border border-slate-200">
                <span className="text-sm text-slate-600">Stolen Vehicle Check</span>
                <span className={clsx(
                  'flex items-center gap-1.5 text-sm font-medium',
                  listing.stolenCheck === 'Passed' ? 'text-green-600' : 'text-red-600'
                )}>
                  {listing.stolenCheck === 'Passed' ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <AlertTriangle className="w-4 h-4" />
                  )}
                  {listing.stolenCheck}
                </span>
              </div>
            )}
            {listing.damageCheck && (
              <div className="flex items-center justify-between bg-white rounded-lg px-3 sm:px-4 py-2.5 border border-slate-200">
                <span className="text-sm text-slate-600">Damaged Import Check</span>
                <span className={clsx(
                  'flex items-center gap-1.5 text-sm font-medium',
                  listing.damageCheck === 'Passed' ? 'text-green-600' : 'text-red-600'
                )}>
                  {listing.damageCheck === 'Passed' ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <AlertTriangle className="w-4 h-4" />
                  )}
                  {listing.damageCheck}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// Shipping & Payment section
function ShippingPaymentSection({ listing }: { listing: Listing }) {
  const shippingOptions = (listing as Listing & { shippingOptions?: string[] }).shippingOptions
  const paymentOptions = (listing as Listing & { paymentOptions?: string[] }).paymentOptions

  if (!shippingOptions?.length && !paymentOptions?.length) return null

  return (
    <div className="mb-6 space-y-4">
      {shippingOptions && shippingOptions.length > 0 && (
        <div className="card p-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <Truck className="w-5 h-5 text-slate-500" />
            Shipping & Delivery
          </h3>
          <div className="flex flex-wrap gap-2">
            {shippingOptions.map((option) => (
              <span key={option} className="badge bg-slate-100 text-slate-700">
                {option}
              </span>
            ))}
          </div>
        </div>
      )}

      {paymentOptions && paymentOptions.length > 0 && (
        <div className="card p-4">
          <h3 className="font-semibold mb-3 flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-slate-500" />
            Payment Options
          </h3>
          <div className="flex flex-wrap gap-2">
            {paymentOptions.map((option) => (
              <span key={option} className="badge bg-slate-100 text-slate-700">
                {option}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
